import dayjs from 'dayjs';

import { GAME_INPUTS, GAME_NUMBERS } from '@/components/constants/constants';
import { ScoreFormatConfig } from '@/components/constants/types';
import { getMatchupStatusApiValue, MatchCompletedEnum, MatchStatusApiMap, MatchStatusEnum, MatchupStatusEnum } from '@/domain/constants/constants';
import { GameCompletedEnum } from '@/domain/constants/constants';
import { Matches, SubMatch } from '@/entities';
import { TeamLeaguePlayerInfo } from '@/entities/matches/TeamPlayersInfoData';
import { TieBreakerFormatType } from '@/entities/Matchup';
import { TeamLeaguePlayerStatus } from '@/entities/TeamPlayerInfo';
import { PodDto } from '@/infrastructure/network/data/matches';
import { ScheduleMatchupDto } from '@/infrastructure/network/dto';
import { Matchup } from '@/infrastructure/network/dto/ScheduleMatchupPeriodsDto';
import { OpponentLineupVisibility, PublicLineupVisibility } from '@/infrastructure/network/dto/SeasonDivisionPool';
import { MatchScores } from '@/lib/types';

/**
 * Filters and returns an array of visible game inputs based on the score format.
 *
 * @param scoreFormat - The number of games to display (e.g., 3 for best of 3, 5 for best of 5)
 * @returns An array of game inputs containing only the visible games
 *
 * @example
 * // For best of 3 format (returns G1, G2, G3, and FINAL)
 * const visibleGames = getVisibleGames(3);
 */
export const getVisibleGames = (scoreFormat: number) => {
	return GAME_INPUTS.filter((game, index) => {
		if (game.gameNumber === GAME_NUMBERS.FINAL) {
			if (scoreFormat === 1) {
				return false;
			} else return true;
		}
		return index < scoreFormat;
	});
};

/**
 * Creates an update data object for PATCH request containing only valid game scores
 * and game statuses
 */
export const createMatchScoreUpdateData = (scores: MatchScores, homeTeamUuid: string, awayTeamUuid: string, scoreFormatConfig: ScoreFormatConfig) => {
	const updateData: Record<string, number> = {};

	processGameScores(scores, updateData);
	processGameStatuses(scores, updateData, scoreFormatConfig);

	// Check if match is completed based on score format rules
	const isMatchCompleted = checkMatchCompletion(scores, scoreFormatConfig);

	if (isMatchCompleted) {
		updateData['matchStatus'] = MatchStatusApiMap[MatchStatusEnum.COMPLETED_MATCH_STATUS];
		updateData['matchCompletedType'] = MatchCompletedEnum.PLAYED_MATCH_COMPLETED_TYPE;
		processWinner(scores, updateData, homeTeamUuid, awayTeamUuid);
	} else {
		// Only set to IN_PROGRESS if match is not already completed
		if (scores.matchStatus !== MatchStatusEnum.COMPLETED_MATCH_STATUS) {
			updateData['matchStatus'] = MatchStatusApiMap[MatchStatusEnum.IN_PROGRESS_MATCH_STATUS];
			updateData['matchCompletedType'] = MatchCompletedEnum.MATCH_NOT_COMPLETED_TYPE;
		}
	}

	return updateData;
};

const processGameScores = (scores: MatchScores, updateData: Record<string, number>) => {
	const gameScoreMapping = {
		game1: ['teamOneGameOneScore', 'teamTwoGameOneScore'],
		game2: ['teamOneGameTwoScore', 'teamTwoGameTwoScore'],
		game3: ['teamOneGameThreeScore', 'teamTwoGameThreeScore'],
		game4: ['teamOneGameFourScore', 'teamTwoGameFourScore'],
		game5: ['teamOneGameFiveScore', 'teamTwoGameFiveScore']
		// final: ['teamOneFinalScore', 'teamTwoFinalScore']
	} as const;

	type GameScoreKey = keyof typeof gameScoreMapping;

	Object.entries(gameScoreMapping).forEach(([game, [homeField, awayField]]) => {
		const gameKey = game as GameScoreKey;
		const homeScore = scores.home[gameKey];
		const awayScore = scores.away[gameKey];

		if (homeScore) {
			updateData[homeField] = parseInt(homeScore);
		}

		if (awayScore) {
			updateData[awayField] = parseInt(awayScore);
		}
	});
};

const processGameStatuses = (scores: MatchScores, updateData: Record<string, number>, scoreFormatConfig: ScoreFormatConfig) => {
	const gameStatusMapping = {
		gameOneStatus: 'gameOneStatus',
		gameTwoStatus: 'gameTwoStatus',
		gameThreeStatus: 'gameThreeStatus',
		gameFourStatus: 'gameFourStatus',
		gameFiveStatus: 'gameFiveStatus'
	} as const;

	const gameScoreMapping = {
		gameOneStatus: 'game1',
		gameTwoStatus: 'game2',
		gameThreeStatus: 'game3',
		gameFourStatus: 'game4',
		gameFiveStatus: 'game5'
	} as const;

	type GameStatusKey = keyof typeof gameStatusMapping;
	type GameScoreKey = keyof typeof gameScoreMapping;

	Object.entries(gameStatusMapping).forEach(([statusKey, apiField], index) => {
		const gameStatusKey = statusKey as GameStatusKey;
		const homeStatus = scores.home[gameStatusKey];
		const awayStatus = scores.away[gameStatusKey];

		// Get the corresponding game score
		const gameScoreKey = gameScoreMapping[gameStatusKey] as GameScoreKey;
		const homeScore = scores.home[gameScoreKey] ? parseInt(scores.home[gameScoreKey]) : 0;
		const awayScore = scores.away[gameScoreKey] ? parseInt(scores.away[gameScoreKey]) : 0;

		// If both scores are 0-0, mark as not completed
		if (homeScore === 0 && awayScore === 0) {
			updateData[apiField] = GameCompletedEnum.GAME_NOT_COMPLETED_TYPE;
			return;
		}

		// Default to played if both teams have scores
		if (homeStatus && awayStatus && homeStatus !== 'FF' && homeStatus !== 'RET' && awayStatus !== 'FF' && awayStatus !== 'RET') {
			const gameConfig = scoreFormatConfig.games[index];
			if (gameConfig) {
				const { max: gameMaxScore, winBy } = gameConfig;
				const maxScore = Math.max(homeScore, awayScore);
				const minScore = Math.min(homeScore, awayScore);
				const scoreDifference = maxScore - minScore;

				// Only set as played if the game meets the winning conditions
				if (maxScore >= gameMaxScore && scoreDifference >= winBy) {
					updateData[apiField] = GameCompletedEnum.PLAYED_COMPLETED_TYPE;
				} else {
					updateData[apiField] = GameCompletedEnum.GAME_NOT_COMPLETED_TYPE;
				}
			}
		}

		if (homeStatus === 'FF') {
			updateData[apiField] = GameCompletedEnum.FORFEIT_TEAM_ONE_GAME_COMPLETED_TYPE;
		} else if (homeStatus === 'RET') {
			updateData[apiField] = GameCompletedEnum.RETIREMENT_TEAM_ONE_GAME_COMPLETED_TYPE;
		}

		if (awayStatus === 'FF') {
			updateData[apiField] = GameCompletedEnum.FORFEIT_TEAM_TWO_GAME_COMPLETED_TYPE;
		} else if (awayStatus === 'RET') {
			updateData[apiField] = GameCompletedEnum.RETIREMENT_TEAM_TWO_GAME_COMPLETED_TYPE;
		}
		// Undo Forfeit/Retirement
		else if (awayStatus === 'UNDO_FF' || awayStatus === 'UNDO_RET' || homeStatus === 'UNDO_FF' || homeStatus === 'UNDO_RET') {
			updateData[apiField] = GameCompletedEnum.PLAYED_COMPLETED_TYPE;
		}
		// If no scores are provided, mark as not played
		if (!homeStatus && !awayStatus) {
			updateData[apiField] = GameCompletedEnum.GAME_NOT_COMPLETED_TYPE;
		}
	});
};

/**
 * Checks if the match is completed based on score format rules
 */
const checkMatchCompletion = (scores: MatchScores, scoreFormatConfig: ScoreFormatConfig): boolean => {
	const { gameBest, gameBestOutOf, maxGames, games } = scoreFormatConfig;

	let homeWins = 0;
	let awayWins = 0;

	const gameScoreMapping = {
		game1: ['teamOneGameOneScore', 'teamTwoGameOneScore'],
		game2: ['teamOneGameTwoScore', 'teamTwoGameTwoScore'],
		game3: ['teamOneGameThreeScore', 'teamTwoGameThreeScore'],
		game4: ['teamOneGameFourScore', 'teamTwoGameFourScore'],
		game5: ['teamOneGameFiveScore', 'teamTwoGameFiveScore']
	} as const;

	type GameScoreKey = keyof typeof gameScoreMapping;

	let gamesPlayed = 0;

	Object.entries(gameScoreMapping).forEach(([game], index) => {
		const gameKey = game as GameScoreKey;
		const homeScore = scores.home[gameKey] ? parseInt(scores.home[gameKey]) : 0;
		const awayScore = scores.away[gameKey] ? parseInt(scores.away[gameKey]) : 0;

		// Skip if no scores
		if (homeScore === 0 && awayScore === 0) return;

		gamesPlayed++;

		const gameConfig = games[index];
		if (!gameConfig) return;

		const { max: gameMaxScore, winBy } = gameConfig;
		const maxScore = Math.max(homeScore, awayScore);
		const minScore = Math.min(homeScore, awayScore);
		const scoreDifference = maxScore - minScore;

		// Check if the game meets the winning conditions
		if (maxScore >= gameMaxScore && scoreDifference >= winBy) {
			if (homeScore > awayScore) {
				homeWins++;
			} else if (awayScore > homeScore) {
				awayWins++;
			}
		}
	});

	if (gameBest === gameBestOutOf && gameBestOutOf === maxGames) {
		if (gamesPlayed === maxGames) {
			return true;
		}
		return false;
	}

	return homeWins >= gameBest || awayWins >= gameBest;
};

/**
 * Determines the winner based on game scores and updates the data object
 * with winner information
 */
const processWinner = (scores: MatchScores, updateData: Record<string, number | string>, homeTeamUuid: string, awayTeamUuid: string) => {
	// Skip winner processing if match is not completed
	if (updateData['matchStatus'] !== MatchStatusApiMap[MatchStatusEnum.COMPLETED_MATCH_STATUS]) {
		return;
	}

	// Add match completion timestamp in ISO format
	updateData['dateCompleted'] = new Date().toISOString();

	// Count wins for each team
	let homeWins = 0;
	let awayWins = 0;

	const gameScoreMapping = {
		game1: ['teamOneGameOneScore', 'teamTwoGameOneScore'],
		game2: ['teamOneGameTwoScore', 'teamTwoGameTwoScore'],
		game3: ['teamOneGameThreeScore', 'teamTwoGameThreeScore'],
		game4: ['teamOneGameFourScore', 'teamTwoGameFourScore'],
		game5: ['teamOneGameFiveScore', 'teamTwoGameFiveScore']
	} as const;

	type GameScoreKey = keyof typeof gameScoreMapping;

	Object.entries(gameScoreMapping).forEach(([game]) => {
		const gameKey = game as GameScoreKey;
		const homeScore = scores.home[gameKey] ? parseInt(scores.home[gameKey]) : 0;
		const awayScore = scores.away[gameKey] ? parseInt(scores.away[gameKey]) : 0;

		// Check if game has valid scores
		if (homeScore > 0 || awayScore > 0) {
			if (homeScore > awayScore) {
				homeWins++;
			} else if (awayScore > homeScore) {
				awayWins++;
			}
		}
	});

	// Determine winner
	if (homeWins > awayWins) {
		updateData['winner'] = 1;
		if (homeTeamUuid) updateData['teamWinnerUuid'] = homeTeamUuid;
		if (awayTeamUuid) updateData['teamLoserUuid'] = awayTeamUuid;
	} else if (awayWins > homeWins) {
		updateData['winner'] = 2;
		if (awayTeamUuid) updateData['teamWinnerUuid'] = awayTeamUuid;
		if (homeTeamUuid) updateData['teamLoserUuid'] = homeTeamUuid;
	} else {
		updateData['winner'] = 0;
	}
};

export const getGameNumberValue = (gameNumber: string): number => {
	return parseInt(gameNumber.replace('G', ''), 10);
};

export const gameNumbers = ['One', 'Two', 'Three', 'Four', 'Five'];

export const getGameStatusKey = (index: number): string => {
	return `game${gameNumbers[index - 1]}Status`;
};

export const getDivisionLevel = (level: string): string => {
	switch (level) {
		case 'DIVISION_LEVEL_UNDEFINED':
			return '-';
		case 'DIVISION_LEVEL_PROFESSIONAL':
			return 'Pro';
		case 'DIVISION_LEVEL_AMATEUR':
			return 'Amateur';
		case 'DIVISION_LEVEL_SR_PRO':
			return 'Senior Pro';
		case 'DIVISION_LEVEL_JR_PRO':
			return 'Junior Pro';
		case 'DIVISION_LEVEL_JR_AMATEUR':
			return 'Junior Amateur';
		case 'DIVISION_LEVEL_SR_AMATEUR':
			return 'Senior Amateur';
		default:
			return '-';
	}
};

export const getDivisionGender = (gender: string): string => {
	switch (gender) {
		case 'PLAYER_GENDER_UNDEFINED':
			return '-';
		case 'PLAYER_GENDER_ANYONE':
			return 'Any';
		case 'PLAYER_GENDER_MALE_ONLY':
			return 'Male';
		case 'PLAYER_GENDER_FEMALE_ONLY':
			return 'Female';
		default:
			return '-';
	}
};

export const formatLastFiveMatchups = (matchups: ScheduleMatchupDto[] = []): Matchup[] =>
	matchups.map((matchup) => ({
		uuid: matchup.uuid,
		date: dayjs(matchup.plannedStartDate).format('MMM D, YYYY'),
		teamOneTitle: matchup.teamOneTitle,
		teamTwoTitle: matchup.teamTwoTitle,
		teamOneLogo: matchup.teamOneLogo?.url ?? '',
		teamTwoLogo: matchup.teamTwoLogo?.url ?? '',
		teamOneScore: matchup.teamOneScore ?? 0,
		teamTwoScore: matchup.teamTwoScore ?? 0,
		matchupId: matchup.uuid
	}));

export const formatPlayerName = (firstName?: string | null, lastName?: string | null) => {
	return firstName && lastName ? `${firstName} ${lastName}` : null;
};

/**
 * Universal helper function to extract enum options for UI components
 *
 * @example
 *  With MatchStatusApiMap (enum keys to number values)
 *  const matchOptions = extractEnumOptions(MatchStatusApiMap);
 *  Result: [{ label: "Completed match status", value: "4" }, ...]
 *
 *  With string enum
 *  const playerOptions = extractEnumOptions(PlayerRole);
 *  Result: [{ label: "Team league team player", value: "TEAM_LEAGUE_TEAM_PLAYER" }, ...]
 *
 *  With custom transformation
 *  const customOptions = extractEnumOptions(MatchStatusApiMap, {
 *   labelTransform: (key) => key.replace('_STATUS', '')
 * });
 */
export function extractEnumOptions(
	enumObject: Record<string, string | number>,
	options?: {
		labelTransform?: (key: string) => string;
	}
): Array<{ label: string; value: string }> {
	const {
		labelTransform = (key: string) =>
			key
				.toLowerCase()
				.replace(/_/g, ' ')
				.replace(/^./, (char) => char.toUpperCase())
	} = options || {};

	return Object.entries(enumObject).map(([key, value]) => ({
		label: labelTransform(key),
		value: value.toString()
	}));
}

export const getFormattedOffset = (): string => {
	const tz = dayjs.tz.guess();
	const offsetMinutes = dayjs().tz(tz).utcOffset();
	const sign = offsetMinutes >= 0 ? '+' : '-';
	const hours = Math.floor(Math.abs(offsetMinutes) / 60);
	const minutes = Math.abs(offsetMinutes) % 60;

	return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

export const getPlayerStatusInfo = (status?: TeamLeaguePlayerStatus) => {
	if (status === TeamLeaguePlayerStatus.TEAM_CAPTAIN) {
		return { label: 'Captain', className: 'text-purple-600' };
	}
	if (status === TeamLeaguePlayerStatus.TEAM_CAPTAIN_ASSISTANCE) {
		return { label: 'Co-captain', className: 'text-gray-500' };
	}
	return { label: '', className: '' };
};

export const isScoreIrregular = (matchRules: ScoreFormatConfig, submittedScores: Record<string, number>): string[] => {
	const irregularities: string[] = [];
	const { maxGames, games, gameBest } = matchRules;

	// Group scores by game
	const gameScores: { team1: number; team2: number }[] = [];

	for (let i = 1; i <= maxGames; i++) {
		const team1Key = `teamOneGame${getGameNumberName(i)}Score`;
		const team2Key = `teamTwoGame${getGameNumberName(i)}Score`;

		if (submittedScores[team1Key] !== undefined || submittedScores[team2Key] !== undefined) {
			gameScores[i - 1] = {
				team1: submittedScores[team1Key] || 0,
				team2: submittedScores[team2Key] || 0
			};
		}
	}

	// Check for skipped games (games with no score followed by games with scores)
	let firstPlayedGameIndex = -1;
	let lastPlayedGameIndex = -1;
	let firstSkippedGameIndex = -1;

	for (let i = 0; i < gameScores.length; i++) {
		const scores = gameScores[i];
		const isGamePlayed = scores && (scores.team1 > 0 || scores.team2 > 0);

		if (isGamePlayed) {
			// Record the first played
			if (firstPlayedGameIndex === -1) {
				firstPlayedGameIndex = i;
			}

			// If we already found a skipped game and now found a played game after it
			if (firstSkippedGameIndex !== -1) {
				irregularities.push(
					`Game ${firstSkippedGameIndex + 1} was skipped but Game ${i + 1} has scores. Games must be played in sequence without skipping any.`
				);
				// Reset so we only report the first instance
				firstSkippedGameIndex = -1;
			}
			lastPlayedGameIndex = i;
		} else if (lastPlayedGameIndex !== -1 && firstSkippedGameIndex === -1) {
			// This is the first skipped game after a played game
			firstSkippedGameIndex = i;
		}
	}

	// Check if the first game was skipped (if we have any played games but the first one isn't)
	if (firstPlayedGameIndex > 0 && firstPlayedGameIndex !== -1) {
		irregularities.push(
			`Game 1 was skipped but Game ${firstPlayedGameIndex + 1} has scores. Games must be played in sequence starting from Game 1.`
		);
	}

	// Check each game's score validity
	gameScores.forEach((scores, index) => {
		if (!scores) return;

		const gameNumber = index + 1;
		const gameConfig = games[index];

		if (!gameConfig) return;

		const { max: gameMaxScore, winBy } = gameConfig;

		// Skip validation for games that don't have both team scores
		if (scores.team1 === 0 && scores.team2 === 0) return;

		// Check if any score is negative
		if (scores.team1 < 0 || scores.team2 < 0) {
			irregularities.push(`Game ${gameNumber}: Scores can't be negative.`);
			return; // Skip further validation for this game
		}

		const maxScore = Math.max(scores.team1, scores.team2);
		const minScore = Math.min(scores.team1, scores.team2);
		const scoreDifference = maxScore - minScore;
		const winningTeam = scores.team1 > scores.team2 ? 'Team 1' : 'Team 2';

		// Check for tied scores below the max score | example: 10-10 at 11 points
		if (scores.team1 === scores.team2 && scores.team1 < gameMaxScore) {
			// Only flag if both scores are significant (not just 0-0)
			if (scores.team1 > 0) {
				irregularities.push(
					`Game ${gameNumber}: The score is ${scores.team1}-${scores.team2}. Games must be played until a team reaches at least ${gameMaxScore} points.`
				);
				return; // Skip further validation for this game
			}
		}

		// Check if both scores are below the max score (incomplete game) | example: 8-7 at 11 points
		if (scores.team1 < gameMaxScore && scores.team2 < gameMaxScore) {
			// Only flag as irregular if there's a winner declared (one score is higher than the other)
			if (scores.team1 !== scores.team2) {
				irregularities.push(`Game ${gameNumber}: You've entered ${maxScore} points, but games are played to ${gameMaxScore}.`);
				return; // Skip further validation for this game
			}
		}

		// Check if the game has reached the max score | example: 12-11 at 11
		if (maxScore >= gameMaxScore) {
			// Check if the winning margin is correct
			if (scoreDifference < winBy) {
				irregularities.push(
					`Game ${gameNumber}: ${winningTeam} needs to win by ${winBy} points! Current lead is only ${scoreDifference} (${maxScore}-${minScore}).`
				);
				return; // Skip further validation for this game
			}
		}

		// Check if the game exceeds max score but doesn't have the required point difference | example: 69-11 at 11 points
		if (maxScore > gameMaxScore && scoreDifference !== winBy) {
			irregularities.push(
				`Game ${gameNumber}: You've entered ${maxScore} points (over the max ${gameMaxScore}), the winning margin must be exactly ${winBy} points. Current difference is ${scoreDifference}.`
			);
		}
	});

	// Check for unnecessary games after match winner is determined, example: 3-0 but gameBest is 2
	if (gameBest) {
		let team1Wins = 0;
		let team2Wins = 0;
		let matchDecided = false;
		let gamesPlayed = 0;

		for (let i = 0; i < gameScores.length; i++) {
			const scores = gameScores[i];
			if (!scores || (scores.team1 === 0 && scores.team2 === 0)) continue;

			gamesPlayed++;

			if (scores.team1 > scores.team2) {
				team1Wins++;
			} else if (scores.team2 > scores.team1) {
				team2Wins++;
			}

			// Check if match is already decided
			if (matchDecided) {
				const winningTeam = team1Wins > team2Wins ? 'Team 1' : 'Team 2';
				irregularities.push(`Match was already decided (${winningTeam} reached ${gameBest} wins). Game ${i + 1} should not be scored.`);
			}

			// Mark match as decided if either team reached gameBest
			if (!matchDecided && (team1Wins >= gameBest || team2Wins >= gameBest)) {
				matchDecided = true;
			}
		}

		// Check if no games have been played (0-0) or match is incomplete
		if (!matchDecided) {
			if (gamesPlayed === 0) {
				// No games played case (0-0)
				irregularities.push(`No games have been scored. A team needs ${gameBest} wins to win the match. Please enter scores for the games.`);
			} else if (gamesPlayed < maxGames) {
				// Incomplete match case
				const team1NeedsWins = gameBest - team1Wins;
				const team2NeedsWins = gameBest - team2Wins;

				// If there are remaining games that could determine a winner
				if (team1NeedsWins > 0 && team2NeedsWins > 0) {
					// Calculate if remaining games are enough for either team to win
					const remainingGames = maxGames - gamesPlayed;

					if (remainingGames >= Math.min(team1NeedsWins, team2NeedsWins)) {
						irregularities.push(
							`Match is incomplete. Current score is ${team1Wins}-${team2Wins}, but a team needs ${gameBest} wins to win the match. Please complete the remaining game(s).`
						);
					}
				}
			}
		}
	}

	return irregularities;
};

// Helper function to convert game number to string name (1 -> "One", 2 -> "Two", etc.)
export const getGameNumberName = (gameNumber: number): string => {
	const gameNumbers = ['One', 'Two', 'Three', 'Four', 'Five'];
	return gameNumbers[gameNumber - 1] || '';
};

export const filterPlayersByGenderRule = (players: TeamLeaguePlayerInfo[], playerGroupTitle: string): TeamLeaguePlayerInfo[] => {
	const groupTitle = playerGroupTitle?.toLowerCase() || '';

	// For Mens matches, only male players allowed
	if (groupTitle === 'mens') {
		return players.filter((player) => player.gender?.toLowerCase() === 'm');
	}
	// For Womens matches, only female players allowed
	else if (groupTitle === 'womens') {
		return players.filter((player) => player.gender?.toLowerCase() === 'f');
	}
	// For Coed or Mixed matches, allow players of any gender
	else if (groupTitle === 'coed' || groupTitle === 'mixed') {
		return players;
	}
	// Default fallback - don't filter if playerGroupTitle is not recognized
	return players;
};

/**
 * Calculates the adjusted scores when a team retires from a match
 *
 * @param gameConfig - The game configuration containing max score and win-by rules
 * @param retiringScore - The score of the retiring team
 * @param opponentScore - The score of the opponent team
 * @returns The required score for the opponent to win
 */
const calculateWinningScore = (gameConfig: { max: number; winBy: number }, retiringScore: number, opponentScore: number): number => {
	// If both scores are above max, ensure win-by difference
	if (retiringScore > gameConfig.max && opponentScore > gameConfig.max) {
		const maxScore = Math.max(retiringScore, opponentScore);
		return maxScore + gameConfig.winBy;
	}

	// If opponent already has max + winBy, keep their score
	if (opponentScore >= gameConfig.max + gameConfig.winBy) {
		return opponentScore;
	}

	// Special case for 0-0 retirement
	if (retiringScore === 0 && opponentScore === 0) {
		return gameConfig.max;
	}

	// If scores are equal (but not 0-0 and above max), opponent needs max + winBy
	if (retiringScore === opponentScore && retiringScore >= gameConfig.max && opponentScore >= gameConfig.max) {
		return gameConfig.max + gameConfig.winBy;
	}

	// If opponent is leading, they need to maintain +2 difference
	if (opponentScore > retiringScore) {
		return Math.max(opponentScore, retiringScore + gameConfig.winBy);
	}

	// If opponent is behind, they need to reach max and have +2 difference
	const requiredScore = Math.max(gameConfig.max, retiringScore + gameConfig.winBy);
	return requiredScore;
};

/**
 * Calculates the adjusted scores when a team retires from a match
 *
 * @param gameNumber - The game number being retired from
 * @param games - The game configuration array
 * @param matchScores - The match scores object containing home and away scores
 * @param team - The team that is retiring ('home' or 'away')
 * @returns Object containing the adjusted home and away scores
 */
export const calculateRetirementScores = (
	gameNumber: string,
	games: Array<{ max: number; winBy: number }>,
	matchScores: { home: Record<string, any>; away: Record<string, any> },
	team: 'home' | 'away'
) => {
	const gameIndex = getGameNumberValue(gameNumber) - 1;
	const gameConfig = games?.[gameIndex] || { max: 11, winBy: 2 };
	const homeScore = getGameScore(matchScores, gameNumber, 'home');
	const awayScore = getGameScore(matchScores, gameNumber, 'away');

	if (team === 'home') {
		// Home team is retiring
		return {
			homeScore,
			awayScore: calculateWinningScore(gameConfig, homeScore, awayScore)
		};
	} else {
		// Away team is retiring
		return {
			homeScore: calculateWinningScore(gameConfig, awayScore, homeScore),
			awayScore
		};
	}
};

/**
 * Gets the score for a specific game and team from the match scores
 *
 * @param matchScores - The match scores object containing home and away scores
 * @param gameNumber - The game number to get the score for
 * @param team - The team to get the score for ('home' or 'away')
 * @returns The score for the specified game and team, or 0 if not found
 */
export const getGameScore = (
	matchScores: { home: Record<string, any>; away: Record<string, any> },
	gameNumber: string,
	team: 'home' | 'away'
): number => {
	const gameKey = `game${getGameNumberValue(gameNumber)}` as keyof typeof matchScores.home;
	return Number(matchScores[team][gameKey]) || 0;
};

export const areAllPlayersSelectedByTeam = (matches: Matches, team: 'home' | 'away'): boolean => {
	return matches.matches
		.filter((match) => !match.isTieBreaker)
		.every((match) => {
			if (match.formatTitle === TieBreakerFormatType.DOUBLES) {
				return team === 'home'
					? Boolean(match.teamOnePlayerOneUuid && match.teamOnePlayerTwoUuid)
					: Boolean(match.teamTwoPlayerOneUuid && match.teamTwoPlayerTwoUuid);
			}

			return team === 'home' ? Boolean(match.teamOnePlayerOneUuid) : Boolean(match.teamTwoPlayerOneUuid);
		});
};

export interface PodStats {
	podUuid: string;
	numberOfCompleted: number;
	total: number;
}

export const groupMatchupCountsByPod = (matches: SubMatch[], pods: PodDto[]): PodStats[] => {
	const grouped = matches.reduce<Record<string, PodStats>>((acc, match) => {
		const podUuid = match.teamLeagueSeasonDivisionPodUuid;
		if (!podUuid) return acc;

		if (!acc[podUuid]) {
			acc[podUuid] = {
				podUuid,
				numberOfCompleted: 0,
				total: 0
			};
		}

		const podStats = acc[podUuid];
		if (match.matchStatus === getMatchupStatusApiValue(MatchupStatusEnum.COMPLETED_MATCHUP_STATUS)) {
			acc[podUuid].numberOfCompleted++;
		}
		podStats.total++;

		return acc;
	}, {});
	const result = pods.map((pod) => grouped[pod.uuid]!);
	return result;
};

export const isLeagueRegistrationOpen = (registrationDateOpen?: Date | null, registrationDateClose?: Date | null): boolean => {
	if (!registrationDateOpen || !registrationDateClose) return false;

	const now = dayjs();
	return now.isAfter(dayjs(registrationDateOpen)) && now.isBefore(dayjs(registrationDateClose));
};

export const determineAreAllPlayersSelected = (isHomeTeamCaptain: boolean, isAwayTeamCaptain: boolean, matchupData: Matches): boolean => {
	return matchupData.matches
		.filter((match) => !match.isTieBreaker && match.matchStatus !== MatchCompletedEnum.CANCELED_MATCH_COMPLETED_TYPE)
		.every((match) => {
			if (match.formatTitle === 'Doubles') {
				const players = isHomeTeamCaptain
					? match.teamOnePlayerOneUuid && match.teamOnePlayerTwoUuid
					: isAwayTeamCaptain
						? match.teamTwoPlayerOneUuid && match.teamTwoPlayerTwoUuid
						: match.teamOnePlayerOneUuid && match.teamOnePlayerTwoUuid && match.teamTwoPlayerOneUuid && match.teamTwoPlayerTwoUuid;

				return Boolean(players);
			}
			const players = isHomeTeamCaptain
				? match.teamOnePlayerOneUuid
				: isAwayTeamCaptain
					? match.teamTwoPlayerOneUuid
					: match.teamOnePlayerOneUuid && match.teamTwoPlayerOneUuid;
			return Boolean(players);
		});
};

export const determinePlayerNameToDisplay = (
	shouldAlwaysShowLineups: boolean,
	isUserCaptain: boolean,
	opponentLineupVisibility: OpponentLineupVisibility,
	captainTeamSide: 'home' | 'away',
	areAllLineupsSelected: boolean,
	publicLineupVisibility?: PublicLineupVisibility,
	playerOneName?: string | null,
	playerTwoName?: string | null,
	matchupStartDateString?: string,
	eventStartDateString?: string,
	ianaTzIdentifier?: string,
	playerOnePartnerName?: string | null,
	playerTwoPartnerName?: string | null,
	lineupPublishedDate?: string | null,
	isDoubleMatch?: boolean
) => {
	if (shouldAlwaysShowLineups) {
		return {
			playerOneName,
			playerTwoName,
			playerOnePartnerName,
			playerTwoPartnerName
		};
	}

	const notVisible = {
		playerOneName: playerOneName ? `Lineup isn't visible at the moment.` : '',
		playerTwoName: isDoubleMatch ? '' : playerTwoName ? `Lineup isn't visible at the moment.` : '',
		playerOnePartnerName: '',
		playerTwoPartnerName: ''
	};

	const visiblePlayerNames = {
		playerOneName,
		playerTwoName,
		playerOnePartnerName,
		playerTwoPartnerName
	};

	if (isUserCaptain) {
		const isHomeTeamCaptain = captainTeamSide === 'home';
		const isAwayTeamCaptain = captainTeamSide === 'away';
		switch (opponentLineupVisibility) {
			case OpponentLineupVisibility.ALWAYS_SHOW_OPPONENT_LINEUP: {
				return {
					playerOneName,
					playerTwoName,
					playerOnePartnerName,
					playerTwoPartnerName
				};
			}
			case OpponentLineupVisibility.HIDE_UNTIL_BOTH_SELECTED_OPPONENT_LINEUP: {
				return {
					playerOneName: isHomeTeamCaptain || areAllLineupsSelected ? playerOneName : `Lineup isn't visible at the moment.`,
					playerTwoName: isAwayTeamCaptain || areAllLineupsSelected ? playerTwoName : `Lineup isn't visible at the moment.`,
					playerOnePartnerName: isHomeTeamCaptain || areAllLineupsSelected ? playerOnePartnerName : ``,
					playerTwoPartnerName: isAwayTeamCaptain || areAllLineupsSelected ? playerTwoPartnerName : ``
				};
			}
			case OpponentLineupVisibility.SHOW_WHEN_LINEUPS_ARE_PUBLISHED: {
				const publishedDate = ianaTzIdentifier ? dayjs.utc(lineupPublishedDate).tz(ianaTzIdentifier) : dayjs.utc(lineupPublishedDate);
				const currentTime = ianaTzIdentifier ? dayjs().tz(ianaTzIdentifier) : dayjs();

				const shouldShowLineups = currentTime.unix() >= publishedDate.unix();
				if (!lineupPublishedDate || !shouldShowLineups) {
					return {
						playerOneName: isHomeTeamCaptain ? playerOneName : `Lineup isn't visible at the moment.`,
						playerTwoName: isAwayTeamCaptain ? playerTwoName : `Lineup isn't visible at the moment.`,
						playerOnePartnerName: isHomeTeamCaptain ? playerOnePartnerName : ``,
						playerTwoPartnerName: isAwayTeamCaptain ? playerTwoPartnerName : ``
					};
				}

				return visiblePlayerNames;
			}
		}
	}

	const matchupStartDate = dayjs.utc(matchupStartDateString).tz(ianaTzIdentifier);
	const eventStartDate = dayjs.utc(eventStartDateString).tz(ianaTzIdentifier);
	const now = dayjs().tz(ianaTzIdentifier);

	switch (publicLineupVisibility) {
		case PublicLineupVisibility.NOT_UNTIL_GAME_DAY_PUBLIC_LINEUP_VISIBILITY: {
			if (now.isSame(matchupStartDate, 'day') || now.isAfter(matchupStartDate, 'day')) {
				return visiblePlayerNames;
			} else {
				return notVisible;
			}
		}
		case PublicLineupVisibility.ONE_DAY_PRIOR_GAME_DAY_PUBLIC_LINEUP_VISIBILITY: {
			if (now.isSame(matchupStartDate.subtract(1, 'day'), 'day') || now.isAfter(matchupStartDate.subtract(1, 'day'), 'day')) {
				return visiblePlayerNames;
			} else {
				return notVisible;
			}
		}
		case PublicLineupVisibility.TWO_DAYS_PRIOR_GAME_DAY_PUBLIC_LINEUP_VISIBILITY: {
			if (now.isSame(matchupStartDate.subtract(2, 'day'), 'day') || now.isAfter(matchupStartDate.subtract(2, 'day'), 'day')) {
				return visiblePlayerNames;
			} else {
				return notVisible;
			}
		}
		case PublicLineupVisibility.THREE_DAYS_PRIOR_GAME_DAY_PUBLIC_LINEUP_VISIBILITY: {
			if (now.isSame(matchupStartDate.subtract(3, 'day'), 'day') || now.isAfter(matchupStartDate.subtract(3, 'day'), 'day')) {
				return visiblePlayerNames;
			} else {
				return notVisible;
			}
		}
		case PublicLineupVisibility.FIRST_DAY_OF_SCHEDULED_EVENT_PUBLIC_LINEUP_VISIBILITY: {
			if (now.isSame(eventStartDate, 'day') || now.isAfter(eventStartDate, 'day')) {
				return visiblePlayerNames;
			} else {
				return notVisible;
			}
		}
		case PublicLineupVisibility.AS_SOON_AS_CREATED_PUBLIC_LINEUP_VISIBILITY: {
			return visiblePlayerNames;
		}
		case PublicLineupVisibility.SHOW_BASED_ON_MATCHUP_PUBLIC_LINEUP_VISIBILITY: {
			if (!lineupPublishedDate) {
				return notVisible;
			}

			const publishedDate = ianaTzIdentifier ? dayjs.utc(lineupPublishedDate).tz(ianaTzIdentifier) : dayjs.utc(lineupPublishedDate);
			const currentTime = ianaTzIdentifier ? dayjs().tz(ianaTzIdentifier) : dayjs();

			const shouldShowLineups = currentTime.unix() >= publishedDate.unix();

			return shouldShowLineups ? visiblePlayerNames : notVisible;
		}
		case PublicLineupVisibility.UNDEFINED_PUBLIC_LINEUP_VISIBILITY: {
			return visiblePlayerNames;
		}
		default: {
			return notVisible;
		}
	}
};
